// ⋯ menu for the per-page actions an editor needs: delete, move-to.
// See client/src/sandbox/PageMenu.tsx for the original.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiMoreLine, RiDeleteBinLine, RiFolderTransferLine, RiSearchLine } from 'react-icons/ri';
import { api, type TreeJsonResponse, type TreeNodeResponse } from '../../lib/api.js';
import { PageIcon } from '../PageIcon.js';

interface PageMenuProps {
  pageId: string;
}

export function PageMenu({ pageId }: PageMenuProps) {
  const [open, setOpen] = useState(false);
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: tree } = useQuery({
    queryKey: ['tree'],
    queryFn: () => api.getTree(),
  });

  useEffect(() => {
    if (!open && !movePickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setMovePickerOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setMovePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, movePickerOpen]);

  const currentPage = tree?.pages.find((p) => p.id === pageId);

  async function handleDelete() {
    if (!currentPage) return;
    const confirmMsg =
      tree && tree.pages.some((p) => p.parent === pageId)
        ? `Delete "${currentPage.title || 'Untitled'}"? Its sub-pages will be moved to the top level.`
        : `Delete "${currentPage.title || 'Untitled'}"?`;
    if (!window.confirm(confirmMsg)) return;
    setOpen(false);

    const parent = currentPage.parent;
    queryClient.setQueryData<TreeJsonResponse>(['tree'], (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        pages: prev.pages
          .filter((p) => p.id !== pageId)
          .map((p) => (p.parent === pageId ? { ...p, parent: null } : p)),
      };
    });
    try {
      await api.deletePage(pageId);
    } catch (err) {
      console.error('deletePage failed', err);
      queryClient.invalidateQueries({ queryKey: ['tree'] });
      return;
    }
    navigate(parent ? `/p/${parent}` : '/');
  }

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        onClick={() => {
          setOpen((o) => !o);
          setMovePickerOpen(false);
        }}
        className="p-1 text-brand-500 hover:bg-brand-100 hover:text-brand-700"
        aria-label="Page actions"
        title="Page actions"
      >
        <RiMoreLine size={18} />
      </button>

      {open && !movePickerOpen && (
        <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-brand-200 shadow-md text-sm">
          <button
            onClick={() => setMovePickerOpen(true)}
            className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center gap-2 text-brand-700"
          >
            <RiFolderTransferLine size={16} />
            Move to…
          </button>
          <button
            onClick={handleDelete}
            className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center gap-2 text-red-700"
          >
            <RiDeleteBinLine size={16} />
            Delete page
          </button>
        </div>
      )}

      {movePickerOpen && tree && (
        <MovePicker
          tree={tree}
          pageId={pageId}
          onClose={() => {
            setMovePickerOpen(false);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function MovePicker({
  tree,
  pageId,
  onClose,
}: {
  tree: TreeJsonResponse;
  pageId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const candidates = useMemo(() => {
    const descendantIds = collectDescendantIds(tree, pageId);
    descendantIds.add(pageId);
    return tree.pages
      .filter((p) => !descendantIds.has(p.id))
      .filter((p) => {
        if (!query.trim()) return true;
        return p.title.toLowerCase().includes(query.toLowerCase());
      })
      .sort((a, b) => a.title.localeCompare(b.title))
      .slice(0, 50);
  }, [tree, pageId, query]);

  async function applyMove(newParent: string | null) {
    onClose();
    queryClient.setQueryData<TreeJsonResponse>(['tree'], (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        pages: prev.pages.map((p) =>
          p.id === pageId ? { ...p, parent: newParent } : p,
        ),
      };
    });
    try {
      await api.movePage(pageId, { parentId: newParent });
    } catch (err) {
      console.error('movePage failed', err);
      queryClient.invalidateQueries({ queryKey: ['tree'] });
    }
  }

  return (
    <div className="absolute right-0 top-full mt-1 z-20 w-80 bg-white border border-brand-200 shadow-md text-sm">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-brand-100">
        <RiSearchLine size={14} className="text-brand-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search pages…"
          className="flex-1 outline-none bg-transparent text-brand-800 placeholder:text-brand-300"
        />
      </div>
      <button
        onClick={() => applyMove(null)}
        className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center gap-2 text-brand-700 italic border-b border-brand-100"
      >
        <span aria-hidden>↑</span>
        Move to top level
      </button>
      <div className="max-h-72 overflow-y-auto">
        {candidates.length === 0 ? (
          <div className="px-3 py-4 text-brand-400 text-center">No matches</div>
        ) : (
          candidates.map((p) => (
            <button
              key={p.id}
              onClick={() => applyMove(p.id)}
              className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center gap-2 text-brand-700"
            >
              <PageIcon icon={p.icon} size="md" alt="" fallback="dot" />
              <span className="truncate">{p.title || 'Untitled'}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function collectDescendantIds(tree: TreeJsonResponse, rootId: string): Set<string> {
  const out = new Set<string>();
  const childrenByParent = new Map<string, TreeNodeResponse[]>();
  for (const p of tree.pages) {
    const key = p.parent ?? '__root__';
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key)!.push(p);
  }
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop()!;
    const children = childrenByParent.get(cur) ?? [];
    for (const c of children) {
      if (!out.has(c.id)) {
        out.add(c.id);
        stack.push(c.id);
      }
    }
  }
  return out;
}
